// Ügyfél-űrlap (create + edit közös, full-page) — react-hook-form + a @atlas/contracts create-sémája
// (zodResolver). A form-INSTANCE-t a route birtokolja (a 422 → mező-hiba a mutációból `form.setError`-rel
// írható), ez a komponens prezentációs: a mezőket + az akció-gombokat rendeli.
import { CLIENT_STATUSES } from '@atlas/shared';
import { type TFunction } from 'i18next';
import { type ReactNode } from 'react';
import { type UseFormReturn } from 'react-hook-form';

import { PillField, SwitchField, TextField } from '@/components/shared/form-fields';
import { FormSection } from '@/components/shared/form-section';
import { Button } from '@/components/ui/button';
import { Form } from '@/components/ui/form';
import { type ClientFormValues } from '@/features/clients/form';
import { CLIENT_SECTION } from '@/features/clients/sections';
import { CLIENT_STATUS_META } from '@/features/clients/status';

interface ClientFormProps {
  form: UseFormReturn<ClientFormValues>;
  mode: 'create' | 'edit';
  /** View-only (nincs clients.manage): a mezők disabled + a mentés rejtve. */
  disabled?: boolean;
  pending?: boolean;
  onSubmit: (values: ClientFormValues) => void;
  t: TFunction;
  /**
   * Miért NEM kapcsolható be az automatikus számlázás (ha nem kapcsolható). A kapcsoló ilyenkor zárolt, és a
   * hint MEGMONDJA az okot — egy indoklás nélkül szürke kapcsoló csak találgatásra késztet.
   *   • `create`          — létrehozáskor még nincs ügyfél, tehát számlázási profil sem köthető hozzá,
   *                         így elsődlegest sem lehet kijelölni. A kapcsoló fogalmilag értelmezhetetlen.
   *   • `noPrimaryProfile` — az ügyfélnek nincs elsődleges számlázási profilja, amiről a számla kiállna.
   * `undefined` → szabadon kapcsolható.
   *
   * FONTOS (legacy-védelem): ha a kapcsoló MÁR BE VAN kapcsolva elsődleges profil nélkül (import-maradvány),
   * a hívó NE adja meg ezt a propot — az állapotot nem némítjuk el és nem is írjuk felül, csak új, hibás
   * bekapcsolást akadályozunk meg. A kikapcsolásnak mindig mennie kell.
   */
  autoBillingBlockedReason?: 'create' | 'noPrimaryProfile' | undefined;
  /**
   * A státusz-pirulák ALATT megjelenő figyelmeztetés — ma az INAKTIVÁLÁS számlázási következménye
   * (`DeactivationBillingNotice`). A hívó adja, mert az adat (a nyitott számlázás-sorok) a detail-oldalé;
   * a form marad megjelenítő.
   */
  statusNotice?: ReactNode;
  /** További szekciók az Alapadatok UTÁN (pl. a létrehozás-oldal zárolt szekciói). A formon BELÜL vannak, de
   *  a mentés-gomb ALATT: a gomb az Alapadatok szekcióhoz tartozik, nem a lap egészéhez (lásd lent). */
  children?: ReactNode;
}

export function ClientForm({
  form,
  mode,
  disabled = false,
  pending = false,
  onSubmit,
  t,
  autoBillingBlockedReason,
  statusNotice,
  children,
}: ClientFormProps): JSX.Element {
  const statusOptions = CLIENT_STATUSES.map((s) => {
    const Icon = CLIENT_STATUS_META[s].icon;
    return {
      value: s,
      label: t(`clients.statusLabel.${s}`),
      icon: <Icon className="h-4 w-4" />,
      activeClassName: CLIENT_STATUS_META[s].pillActiveClass,
    };
  });

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-8">
        {/* Alapadatok — SZEKCIÓ (nem Card): a form-oldalak a profil-oldal mintáját követik, ahol a tagolást a
            szekció-cím + térköz adja, nem keret/háttér (`FormSection`, design.md §2 overline). A számlázási
            profilok read-only listája a client-detail külön szekciójában él, nem a formban. */}
        <FormSection id={CLIENT_SECTION.data} title={t('clients.sectionData')}>
          <div className="space-y-5">
            <TextField<ClientFormValues>
              name="name"
              label={t('clients.name')}
              required
              disabled={disabled}
              autoComplete="off"
            />
            <div className="grid gap-5 sm:grid-cols-2">
              <TextField<ClientFormValues>
                name="email"
                type="email"
                label={t('clients.email')}
                disabled={disabled}
                autoComplete="off"
              />
              <TextField<ClientFormValues>
                name="phone"
                type="tel"
                label={t('clients.phone')}
                disabled={disabled}
                autoComplete="off"
              />
            </div>
            <PillField<ClientFormValues>
              name="status"
              label={t('clients.status')}
              disabled={disabled}
              options={statusOptions}
            />
            {statusNotice}
            {/* AUTOMATIKUS SZÁMLÁZÁS — a kapcsoló feltételhez kötött: számla csak elsődleges számlázási
                profilról állítható ki. Létrehozáskor ez fogalmilag lehetetlen (nincs még ügyfél, amihez
                profilt lehetne kötni), szerkesztéskor pedig azon múlik, van-e elsődleges profil. A zárolás
                MINDIG indokolt: a hint megmondja, MIÉRT nem kapcsolható és mit kell tenni hozzá. */}
            <SwitchField<ClientFormValues>
              name="autoBilling"
              label={t('clients.autoBilling')}
              hint={
                autoBillingBlockedReason
                  ? t(`clients.autoBillingBlocked.${autoBillingBlockedReason}`)
                  : t('clients.autoBillingHint')
              }
              disabled={disabled || autoBillingBlockedReason !== undefined}
            />
          </div>

          {/* AKCIÓ — A SZEKCIÓN BELÜL, a mezők után.
              MIÉRT ITT, és nem a lap alján: ez a gomb KIZÁRÓLAG az Alapadatokat menti. A kapcsolódó szekciók
              (fiókok, szolgáltatások, profilok, kártyák) a saját sheetjükön írnak, azonnal — nincs közös
              beküldés. A lap aljára tett gomb ezt hazudná: „mentsd el az egész oldalt". A szekción belül
              viszont ránézésre ahhoz tartozik, amit tényleg ment (a GitHub settings szekciónkénti mentése).
              Hozadék: a create- és az edit-oldal végre UGYANOTT hozza — korábban a create-en a `children`
              (négy zárolt szekció) a gomb ELŐTT állt, tehát a lap aljára került, az edit-en meg középre.

              JOBBRA igazítva (2026-08-18, user-döntés — felülírja a korábbi balra igazítást). Az ok NEM
              elméleti: az app már ma is a jobb végén hozza az akciókat — lista-toolbar, sheet- és
              modal-lábléc, szekció-fejléc, veszélyzóna, ÉS a másik két oldal-űrlap is (`account-form`,
              `invoice-form`). A balra igazítás három helyen élt (ügyfél + profil), tehát nem konvenció volt,
              hanem kilógás. Egy terméken belül a következetesség erősebb szempont, mint a hosszú, ISMERETLEN
              űrlapokra mért beolvasási előny (Wroblewski) — az Atlas belső rekord-szerkesztő: előre kitöltött
              mezők, napi használat. A szabály így kivétel nélküli: minden akció a konténere jobb végén.
              Hozadék: az ügyfél-detailen egyetlen függőleges akció-tengely lesz (Mentés · + hozzáadás-gombok ·
              Törlés) — eddig a Mentés lógott ki belőle.
              `pt-1`: a szekció `space-y-4`-e + 4px = a mezők közti 20px-es ritmus.
              „Vissza" gomb NINCS: a vissza-navigáció helye a topbar szülő-morzsája („Ügyfelek / …"). */}
          {!disabled && (
            <div className="flex justify-end pt-1">
              <Button type="submit" variant="brand" disabled={pending}>
                {pending
                  ? t('clients.saving')
                  : mode === 'create'
                    ? t('clients.create')
                    : t('clients.save')}
              </Button>
            </div>
          )}
        </FormSection>

        {children}
      </form>
    </Form>
  );
}
